import React, { useState } from "react";
import {
  CreditCard,
  TrendingUp,
  Users,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  FileSpreadsheet,
  FileText,
  X,
  RefreshCw,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Payment, PaymentCategory, PaymentStats } from "../../../../types/features/administration/Transaction";
import TransactionsList from "./TransactionsList";
import TransactionDetailsModal from "./TransactionDetails";
import usePaymentsByCategory from "../../../../hooks/administration/transactions/usePaymentsByCategory";
import useToken from "../../../../hooks/auth/useToken";
import { paymentsService } from "../../../../services/administration/paymentsService";
import UsersTransactionModal from "../users/UsersTrnsactionsModal";

type TransactionType = "transfer" | "deposit";

interface FilterState {
  search: string;
  status: string;
  operator: string;
  provider: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

const TransfertsView: React.FC = () => {
  const { token } = useToken();

  const [transactionType, setTransactionType] = useState<TransactionType>("transfer");
  const [activeTab, setActiveTab] = useState<PaymentCategory>("today");
  const [selectedDate, setSelectedDate] = useState<string>("");

  const {
    payments: apiPayments,
    loading: paymentsLoading,
    getPayments: refreshPayments,
    total: totalTransactionsCount,
    currentPage: serverPage,
    totalPages: serverTotalPages,
    goToPage,
    handleSearch: serverSearch,
  } = usePaymentsByCategory({
    category: activeTab,
    date: selectedDate,
    enabled: true,
  });

  const [filters, setFilters] = useState<FilterState>({
    search: "",
    status: "",
    operator: "",
    provider: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const [isExporting, setIsExporting] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Payment | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [globalStats, setGlobalStats] = useState<PaymentStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showUserTransactionsModal, setShowUserTransactionsModal] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState<{ id: string; name: string } | null>(null);

  const filteredTransactions = apiPayments.filter((transaction) => {
    const typeMatch =
      transactionType === "transfer"
        ? transaction.payment_type !== "wallet_deposit"
        : transaction.payment_type === "wallet_deposit";

    return (
      typeMatch &&
      (filters.status === "" || transaction.status === filters.status) &&
      (filters.operator === "" || transaction.operator === filters.operator) &&
      (filters.provider === "" || transaction.provider === filters.provider)
    );
  });

  const handleFiltersChange = (newFilters: FilterState) => {
    if (newFilters.search !== filters.search) {
      serverSearch(newFilters.search);
    }
    setFilters(newFilters);
  };

  const startIndex = (serverPage - 1) * 20;
  const endIndex = Math.min(startIndex + filteredTransactions.length, totalTransactionsCount);

  const handleTransactionAction = async (action: string, transaction: Payment, data?: any) => {
    switch (action) {
      case "view":
        setSelectedTransaction(transaction);
        break;
      case "view-user-history":
        if (transaction.user_id) {
          setSelectedUserForHistory({
            id: transaction.user_id,
            name: transaction.user ? `${transaction.user.first_name} ${transaction.user.last_name}` : "Utilisateur",
          });
          setShowUserTransactionsModal(true);
        }
        break;
      case "change-status":
        try {
          const payoutStatus = data?.payout_status || "SUCCESS";
          const transactionStatus = data?.transaction_status || "SUCCEEDED";
          const response = await paymentsService.changePaymentStatus(
            token,
            transaction.id,
            payoutStatus,
            transactionStatus
          );
          if (response.message) {
            alert(`Succes: ${response.message}`);
            refreshPayments();
          }
        } catch (error: any) {
          console.error("Erreur:", error);
          alert(`Erreur: ${error.response?.data?.message || error.message}`);
        }
        break;
      case "load-verification-sides":
        return paymentsService.getVerificationSides(token, transaction.id);
      case "check-status": {
        const side = data?.side || "canada";
        return paymentsService.verifyOperatorStatus(token, transaction.id, side);
      }
    }
  };

  const getAllFilteredTransactionsForExport = async (): Promise<Payment[]> => {
    if (!token) return [];

    try {
      setIsExporting(true);
      const allTransactions: Payment[] = [];
      let currentPage = 1;
      let hasMore = true;
      const itemsPerPage = 100;

      while (hasMore) {
        const skip = (currentPage - 1) * itemsPerPage;
        const pagination = {
          skip,
          limit: itemsPerPage,
          search: filters.search || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        };

        let result;
        switch (activeTab) {
          case "today":
            result = await paymentsService.getTodayPayments(token, pagination);
            break;
          case "all":
            result = await paymentsService.getPayments(token, pagination);
            break;
          case "pending":
            result = await paymentsService.getPendingPayments(token, pagination);
            break;
          case "succeeded_payout_pending":
            result = await paymentsService.getSucceededPayoutPending(token, pagination);
            break;
          case "succeeded_payout_failed":
            result = await paymentsService.getSucceededPayoutFailed(token, pagination);
            break;
          case "succeeded_payout_success":
            result = await paymentsService.getSucceededPayoutSuccess(token, pagination);
            break;
          case "failed":
            result = await paymentsService.getFailedPayments(token, pagination);
            break;
          case "by_date":
            if (selectedDate) {
              result = await paymentsService.getPaymentsByDate(token, selectedDate, pagination);
            } else {
              result = { payments: [], total: 0, skip: 0, limit: itemsPerPage };
            }
            break;
          default:
            result = await paymentsService.getPayments(token, pagination);
        }

        const payments = result?.payments || [];
        allTransactions.push(...payments);
        hasMore = payments.length === itemsPerPage && allTransactions.length < result.total;
        currentPage++;
      }

      const filtered = allTransactions.filter((transaction) => {
        const typeMatch =
          transactionType === "transfer"
            ? transaction.payment_type !== "wallet_deposit"
            : transaction.payment_type === "wallet_deposit";
        return (
          typeMatch &&
          (filters.status === "" || transaction.status === filters.status) &&
          (filters.operator === "" || transaction.operator === filters.operator) &&
          (filters.provider === "" || transaction.provider === filters.provider)
        );
      });

      setIsExporting(false);
      return filtered;
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      setIsExporting(false);
      return [];
    }
  };

  const handleExport = async (format: "excel" | "pdf") => {
    setShowExportModal(false);
    const allFilteredTransactions = await getAllFilteredTransactionsForExport();

    if (allFilteredTransactions.length === 0) {
      alert("Aucune transaction à exporter");
      return;
    }

    const exportData = allFilteredTransactions.map((transaction, index) => ({
      "#": index + 1,
      "ID Transaction": transaction.id,
      Date: new Date(transaction.created_at).toLocaleString("fr-FR"),
      Type: transaction.payment_type === "wallet_deposit" ? "Rechargement" : "Transfert",
      Utilisateur: transaction.user
        ? `${(transaction.user as any).first_name} ${(transaction.user as any).last_name}`
        : "N/A",
      Email: transaction.user?.email || "N/A",
      Telephone: transaction.phone,
      "Montant (CAD)": transaction.amount_cad.toFixed(2),
      "Montant (FCFA)": transaction.amount_fcfa.toFixed(0),
      "Frais (CAD)": transaction.fee_cad.toFixed(2),
      "Taux de conversion": transaction.conversion_rate ? transaction.conversion_rate.toFixed(2) : "N/A",
      "Total (CAD)": transaction.total_cad.toFixed(2),
      Operateur: transaction.operator,
      Fournisseur: transaction.provider,
      Statut: transaction.status,
      "Statut Payout": transaction.payout_status || "N/A",
    }));

    if (format === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
      const fileName = `transactions_${transactionType}_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } else {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      doc.setFontSize(18);
      doc.text(`Rapport des ${transactionType === "transfer" ? "Transferts" : "Rechargements"}`, 14, 15);
      doc.setFontSize(11);
      doc.text(`Genere le: ${new Date().toLocaleString("fr-FR")}`, 14, 22);
      doc.text(`Total: ${filteredTransactions.length} transactions`, 14, 28);

      const headers = Object.keys(exportData[0] || {});
      const rows = exportData.map((row: any) => headers.map((header) => row[header]));

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 35,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      const fileName = `transactions_${transactionType}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);
    }
  };

  React.useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      setStatsLoading(true);
      try {
        const stats = await paymentsService.getPaymentStatsByCategory(
          token,
          activeTab === "by_date" ? "by_date" : activeTab,
          activeTab === "by_date" ? selectedDate : undefined
        );
        setGlobalStats(stats);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [activeTab, selectedDate, token]);

  const totalAmount = globalStats?.total_amount || 0;
  const totalFees = globalStats?.total_fees || 0;
  const successfulTransactions = globalStats?.succeeded_count || 0;
  const failedTransactions = globalStats?.failed_count || 0;
  const pendingTransactions = globalStats?.pending_count || 0;
  const succeededAmount = globalStats?.succeeded_amount || 0;
  const failedAmount = globalStats?.failed_amount || 0;
  const pendingAmount = globalStats?.pending_amount || 0;

  const getTodayLabel = () => {
    const today = new Date();
    return today.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  };

  const tabs = [
    { id: "today" as PaymentCategory, label: getTodayLabel(), icon: Calendar, color: "purple" },
    { id: "all" as PaymentCategory, label: "Toutes", icon: CreditCard, color: "green" },
    { id: "pending" as PaymentCategory, label: "En Attente", icon: Clock, color: "yellow" },
    { id: "succeeded_payout_success" as PaymentCategory, label: "Payout Reussi", icon: CheckCircle, color: "green" },
    { id: "succeeded_payout_pending" as PaymentCategory, label: "Payout En Attente", icon: Clock, color: "blue" },
    { id: "succeeded_payout_failed" as PaymentCategory, label: "Payout Echoue", icon: AlertCircle, color: "orange" },
    { id: "failed" as PaymentCategory, label: "Echouees", icon: XCircle, color: "red" },
  ];

  return (
    <>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">Statistiques des Transferts</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Transactions Reussies</p>
                <p className="text-2xl font-bold text-blue-600">{successfulTransactions}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">En Attente</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingTransactions}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Echouees</p>
                <p className="text-2xl font-bold text-red-600">{failedTransactions}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex overflow-x-auto flex-1 space-x-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const getColorClasses = () => {
                  if (!isActive) return "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
                  switch (tab.color) {
                    case "purple":
                      return "border-purple-600 text-purple-600";
                    case "green":
                      return "border-green-600 text-green-600";
                    case "yellow":
                      return "border-yellow-600 text-yellow-600";
                    case "blue":
                      return "border-blue-600 text-blue-600";
                    case "orange":
                      return "border-orange-600 text-orange-600";
                    case "red":
                      return "border-red-600 text-red-600";
                    default:
                      return "border-gray-600 text-gray-600";
                  }
                };
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-3 space-x-2 whitespace-nowrap border-b-2 transition-colors ${getColorClasses()}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={refreshPayments}
              disabled={paymentsLoading}
              className="flex gap-2 items-center p-3 ml-3 font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-lg shadow-md transition-all hover:from-green-700 hover:to-green-800 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${paymentsLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Rafraichir</span>
            </button>
          </div>
        </div>

        <div className="flex items-center mt-4 space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={() => {
                if (selectedDate) {
                  setActiveTab("by_date");
                }
              }}
              disabled={!selectedDate}
              className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300"
            >
              Rechercher
            </button>
          </div>
        </div>
      </div>

      <TransactionsList
        transactions={filteredTransactions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onTransactionAction={handleTransactionAction}
        currentPage={serverPage}
        totalPages={serverTotalPages}
        onPageChange={goToPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalTransactions={filteredTransactions.length}
        loading={paymentsLoading}
      />

      <TransactionDetailsModal
        transaction={selectedTransaction}
        isOpen={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        onTransactionUpdated={refreshPayments}
      />

      <UsersTransactionModal
        isOpen={showUserTransactionsModal}
        onClose={() => {
          setShowUserTransactionsModal(false);
          setSelectedUserForHistory(null);
          refreshPayments();
        }}
        userId={selectedUserForHistory?.id || ""}
        userName={selectedUserForHistory?.name}
        onTransactionUpdated={refreshPayments}
      />
    </>
  );
};

export default TransfertsView;
